import { Handshake, Key, Rocket, ChevronRight, MoveRight, Users, Lightbulb, MessageSquare, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { WhiteCard } from "@/components/ui/white-card";

const benefits = [
  {
    name: "Influence Development",
    description: "Your insights will directly shape Sentiri's features to meet industry needs.",
    icon: Handshake,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    name: "Early Access",
    description: "Gain a competitive edge with exclusive early access, special pricing and much more.",
    icon: Key,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  },
  {
    name: "Stay Ahead",
    description: "Prepare proactively for upcoming regulatory changes before they impact your operations.",
    icon: Rocket,
    gradient: "from-[#00DC82] to-[#00A3FF]"
  }
];
export function EarlyAdopterSection() {
  const [count, setCount] = useState(0);
  // Use integer arithmetic to avoid floating point issues completely
  const percentage = Math.min(Math.round((count / 50) * 100), 100); // Calculate percentage based on 50 spots
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Generate random confetti particles
  const confettiParticles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -20 - Math.random() * 80,
    size: 5 + Math.random() * 10,
    color: i % 3 === 0 ? '#00DC82' : i % 3 === 1 ? '#00B4B4' : '#ffffff',
    rotation: Math.random() * 360,
    delay: Math.random() * 2
  }));

  useEffect(() => {
    async function getWaitlistCount() {
      const { data } = await supabase
        .from('waitlist_count')
        .select('count')
        .single();
      
      if (data) {
        setCount(data.count);
      }
    }

    getWaitlistCount();

    const channel = supabase
      .channel('waitlist_changes')
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'waitlist_count' },
        (payload) => {
          setCount(payload.new.count);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <motion.h2 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold relative mb-2 sm:mb-4 pb-1 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-[#00DC82]/20 to-transparent blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="text-foreground">Exclusive Access for</span> <span className="text-[#00DC82]">Construction Manufacturers</span>
          </motion.h2>
        </div>

        {/* Benefit Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-16">
          {benefits.map((benefit, index) => (
            <motion.div 
              key={benefit.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative bg-card rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 hover:z-30 border-[1.5px] border-[#00DC82]/10 hover:border-[#00DC82] hover:shadow-[#00DC82]/30 hover:shadow-2xl"
            >
              <div className={`absolute inset-0 opacity-[0.03] bg-gradient-to-br ${benefit.gradient}`} />
              
              <div className="relative p-4 sm:p-5 md:p-6">
                <div className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-[#00DC82] group-hover:scale-110 transition-transform duration-500">
                  <benefit.icon className="w-full h-full" />
                </div>
                
                <h3 className="text-xl sm:text-2xl font-semibold text-card-foreground mb-2 md:mb-3 group-hover:text-[#00DC82] transition-colors duration-300">
                  {benefit.name}
                </h3>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
                  {benefit.description}
                </p>

                <div className="absolute inset-0 border-[1.5px] border-transparent rounded-xl transition-all duration-500 group-hover:border-[#00DC82] group-hover:border-opacity-20" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Progress Card */}
        <motion.div 
          className="w-full relative z-20" 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          onViewportEnter={() => setShowConfetti(true)}
          onViewportLeave={() => setShowConfetti(false)}
        >
          {/* Confetti animation */}
          {showConfetti && (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {confettiParticles.map((particle) => (
                <motion.div
                  key={particle.id}
                  className="absolute w-2 h-2 rounded-full"
                  style={{ 
                    left: `${particle.x}%`,
                    backgroundColor: particle.color,
                    width: particle.size,
                    height: particle.size,
                    rotate: particle.rotation,
                  }}
                  initial={{ y: particle.y, opacity: 0 }}
                  animate={{ 
                    y: ['0%', '100%'],
                    opacity: [0, 1, 0],
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 4 + Math.random() * 4,
                    delay: particle.delay,
                    repeat: Infinity,
                    repeatDelay: 2 + Math.random() * 2,
                    ease: [0.1, 1, 0.9, 1]
                  }}
                />
              ))}
            </div>
          )}

          <Card 
            className="group shadow-lg border-[1.5px] border-[#00DC82]/10 overflow-hidden backdrop-blur-sm hover:shadow-2xl hover:border-[#00DC82] hover:shadow-[#00DC82]/30 transition-all duration-500 bg-card"
          >
            <CardHeader className="pb-4 px-5 sm:px-8 md:px-10 pt-6 sm:pt-8 md:pt-10">
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 lg:gap-6">
                <div className="space-y-2"> 
                  <motion.h3 
                    className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold relative pb-2 leading-tight" 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-[#00DC82]/20 to-transparent blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="text-card-foreground">Lead the Future </span><span className="text-[#00DC82]">of</span> <span className="text-[#00DC82]">Compliance</span>
                  </motion.h3>
                  <div className="flex items-center space-x-2">
                    <Users size={16} className="text-[#00DC82] flex-shrink-0" />
                    <p className="text-base sm:text-lg text-muted-foreground">Join other industry leaders in our early access group</p>
                  </div>
                </div>
                <motion.div 
                  className="flex items-center gap-3 relative mt-3 lg:mt-0"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-[#00DC82]/20 via-[#00DC82]/0 to-[#00DC82]/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    animate={{
                      backgroundPosition: ['0% 0%', '100% 0%', '0% 0%'],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                  <motion.div className="relative">
                    <motion.div 
                      className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#00DC82]/20 to-[#00DC82]/20 blur-md opacity-0 group-hover:opacity-100"
                      animate={{
                        scale: [1, 1.2, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                      }}
                    />
                  </motion.div>
                </motion.div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 sm:space-y-8 pt-6 sm:pt-8 pb-8 sm:pb-10 px-5 sm:px-8 md:px-10">
              <div className="relative group-hover:scale-[1.02] transition-transform duration-500">
                {/* Background pattern */}
                <div className="absolute inset-0 rounded-full overflow-hidden shadow-inner bg-[#00DC82]/15 dark:bg-[#00DC82]/25">
                  <motion.div
                    className="absolute inset-0 opacity-40 dark:opacity-60"
                    style={{
                      backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2300DC82' fill-opacity='0.25' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E\")",
                      backgroundColor: 'rgba(0,220,130,0.08)',
                      mixBlendMode: 'lighten',
                    }}
                    animate={{
                      x: [0, -20],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  />
                </div>

                {/* Progress bar */}
                <div className="relative h-4 sm:h-5 md:h-7 rounded-full overflow-hidden group-hover:h-6 sm:group-hover:h-7 md:group-hover:h-8 transition-all duration-500">
                  {/* Main progress fill */}
                  <motion.div
                    className="absolute top-0 left-0 h-full w-full bg-[#00DC82] transition-colors duration-500"
                    initial={{ width: 0 }}
                    whileInView={{ width: `${percentage}%` }}
                    transition={{ 
                      duration: 1.5, 
                      ease: [0.34, 1.56, 0.64, 1],
                      delay: 0.2
                    }}
                  >
                    {/* Shine effects */}
                    <motion.div
                      className="absolute inset-0 w-[50%] bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:via-white/40"
                      animate={{
                        x: ['-100%', '200%'],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        repeatDelay: 0.5
                      }}
                    />

                    {/* Small moving dots for visual flair */}
                    <div className="absolute inset-y-0 left-0 w-full overflow-hidden">
                      <motion.div 
                        className="absolute top-1/2 h-1 w-1 rounded-full bg-white/70"
                        style={{ y: '-50%' }}
                        animate={{
                          x: ['0%', '100%'],
                          opacity: [0, 1, 0]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'linear',
                          repeatDelay: 1
                        }}
                      />
                      <motion.div 
                        className="absolute top-1/4 h-1 w-1 rounded-full bg-white/70"
                        animate={{
                          x: ['0%', '100%'],
                          opacity: [0, 1, 0]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: 'linear',
                          delay: 0.5,
                          repeatDelay: 0.5
                        }}
                      />
                    </div>
                  </motion.div>

                  {/* Pulsing glow effect */}
                  <motion.div
                    className="absolute top-0 left-0 h-full bg-white/20"
                    style={{ width: `${percentage}%` }}
                    animate={{
                      opacity: [0.2, 0.3, 0.2],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                  
                  {/* Edge glow effect */}
                  <motion.div
                    className="absolute top-0 h-full w-4 rounded-r-full bg-white/50"
                    style={{ left: `calc(${percentage}% - 8px)` }}
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                      scale: [0.8, 1, 0.8]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 my-4">
                <div className="flex items-center gap-3 sm:gap-4 group-hover:scale-105 transition-transform duration-500">
                  <div className="relative">
                    <motion.div 
                      className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#00DC82]"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [1, 0.7, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    <motion.div 
                      className="absolute inset-0 w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-[#00DC82]"
                      animate={{
                        scale: [1, 2],
                        opacity: [0.5, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  </div>
                  <span className="text-base sm:text-lg text-muted-foreground font-medium group-hover:text-[#00DC82] transition-colors duration-300">Live: Industry leaders joining</span>
                </div>
              </div>

              {/* Add matching join waitlist button */}
              <motion.div 
                className="mt-6 sm:mt-8"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <Button 
                  size="lg" 
                  className="gap-2 bg-[#00DC82] text-white hover:bg-[#00DC82]/90 w-full py-2 sm:py-4 text-sm sm:text-base" 
                  asChild
                >
                  <Link to="/waitlist">
                    Join Waitlist
                  </Link>
                </Button>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}